import React from "react";
import { Flex } from "@singlestore/fusion/components/layout";
import { H1, Strong } from "@singlestore/fusion/components/typography";
import { cx } from "class-variance-authority";
import { useVerifySignature } from "contexts/VerifySignatureContext";

import { Button } from "components/Button/Button";
import { getVerifyCommand } from "components/DownloadButton/util/commands";
import { InlineCode } from "components/InlineCode/InlineCode";
import { Link } from "components/Link/Link";
import { Modal } from "components/Modal/Modal";
import { Paragraph } from "components/Paragraph/Paragraph";
import { SyntaxBlockGroup } from "components/SyntaxBlock/SyntaxBlockGroup";

import "./VerifySignatureModal.scss";

type VerifySignatureModalProps = {
    className?: string;
};

export function VerifySignatureModal({ className }: VerifySignatureModalProps) {
    const {
        open,
        closeModal,
        releaseJson,
        releaseUrl,
        signatureUrl,
        checksum,
    } = useVerifySignature();

    const classes = cx("verify-signature-modal", className);

    const sigParts = signatureUrl?.split("/");
    const sigFilename = sigParts?.[sigParts.length - 1];

    const verifyCommand = getVerifyCommand(checksum, sigFilename);

    return (
        <Modal open={open} setOpen={closeModal} size="lg" className={classes}>
            <Flex
                flexDirection="column"
                gap="2x"
                p="4x"
                flexGrow="1"
                overflowY="auto"
            >
                <H1 variant="heading-5">Verification instructions</H1>

                <Paragraph variant="body-3">
                    <Strong>Note:</Strong> You must install{" "}
                    <Link to="https://docs.sigstore.dev/cosign/system_config/installation/">
                        cosign
                    </Link>{" "}
                    to verify the authenticity of the SingleStore file.
                </Paragraph>

                <Paragraph variant="body-3">
                    Use the following steps to verify the authenticity of{" "}
                    <InlineCode>singlestoredb-server</InlineCode>,{" "}
                    <InlineCode>singlestoredb-toolbox</InlineCode>,{" "}
                    <InlineCode>singlestoredb-studio</InlineCode>, and{" "}
                    <InlineCode>singlestore-client</InlineCode> SingleStore
                    files that have been downloaded.
                </Paragraph>

                <Paragraph variant="body-3">
                    You may perform the following steps on any computer that can
                    run <InlineCode>cosign</InlineCode>, such as the main
                    deployment host of the cluster.
                </Paragraph>

                <ol>
                    <li>
                        <Paragraph variant="body-3">
                            (Optional) Run the following command to view the
                            associated signature files.
                        </Paragraph>

                        <SyntaxBlockGroup
                            inputBlock={`curl ${releaseUrl}`}
                            outputBlock={JSON.stringify(releaseJson, null, 2)}
                            language="bash"
                        />
                    </li>

                    <li>
                        <Paragraph variant="body-3">
                            Download the signature file from the SingleStore
                            release server.
                        </Paragraph>

                        <ul>
                            <li>
                                <Paragraph variant="body-3">
                                    <Strong>Option 1:</Strong> Click the{" "}
                                    <Strong>Download Signature</Strong> button
                                    next to the SingleStore file.
                                </Paragraph>
                            </li>
                            <li>
                                <Paragraph variant="body-3">
                                    <Strong>Option 2:</Strong> Copy and paste
                                    the following URL into the address bar of
                                    your browser and save the signature file.
                                </Paragraph>

                                <SyntaxBlockGroup
                                    inputBlock={signatureUrl as string}
                                />
                            </li>

                            <li>
                                <Paragraph variant="body-3">
                                    <Strong>Option 3:</Strong> Run the following
                                    command to download the signature file.
                                </Paragraph>

                                <SyntaxBlockGroup
                                    inputBlock={`curl -O ${signatureUrl}`}
                                    language="bash"
                                />
                            </li>
                        </ul>
                    </li>

                    <li>
                        <Paragraph variant="body-3">
                            After the signature file has been downloaded, run
                            the following command to verify the authenticity of
                            the SingleStore file.
                        </Paragraph>

                        <SyntaxBlockGroup
                            inputBlock={verifyCommand}
                            outputBlock="Verified OK"
                            language="bash"
                        />
                    </li>
                </ol>
            </Flex>

            <Flex
                justifyContent="end"
                className="bottom-bar"
                flexGrow="0"
                p="2x"
            >
                <Button onClick={closeModal}>Done</Button>
            </Flex>
        </Modal>
    );
}
